import React, { useState, useRef, useEffect } from 'react';
import { HashtagAutocomplete, CATEGORIAS } from './HashtagAutocomplete';
import { createReport, getResidentesForTags } from '../api';
import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { translateError } from '../utils';

export default function ReporteForm({ user, onBack, onSuccess }) {
  const [categoria, setCategoria] = useState('');
  const [texto, setTexto] = useState('');
  const [hashtags, setHashtags] = useState([]);
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [residentes, setResidentes] = useState([]);
  const [sugerencias, setSugerencias] = useState([]);
  const [showSugerencias, setShowSugerencias] = useState(false);
  const fileRef = useRef(null);
  const reportFileRef = useRef(null);
  const textoRef = useRef(null);

  useEffect(() => {
    getResidentesForTags().then(setResidentes).catch(() => {});
  }, []);

  const handleFoto = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setFotos([...fotos, reader.result]);
      reader.readAsDataURL(file);
    }
  };

  const handleTextChange = (e) => {
    const val = e.target.value;
    setTexto(val);

    // Extraer hashtags completos del texto
    const tags = [];
    const regex = /#(\w+)/g;
    let match;
    while ((match = regex.exec(val)) !== null) {
      tags.push(match[1]);
    }
    setHashtags([...new Set(tags)]);

    // Detectar autocompletado: buscar # seguido de texto sin espacio
    const pos = e.target.selectionStart;
    const beforeCursor = val.substring(0, pos);
    const hashIndex = beforeCursor.lastIndexOf('#');
    
    if (hashIndex !== -1) {
      const afterHash = beforeCursor.substring(hashIndex + 1);
      // Solo sugerir si no hay espacio después del #
      if (!afterHash.includes(' ') && !afterHash.includes('\n')) {
        const search = afterHash.toLowerCase();
        const filtered = residentes.filter(r =>
          r.apartamento?.toLowerCase().startsWith(search) ||
          r.nombre_completo?.toLowerCase().includes(search)
        ).slice(0, 6);
        setSugerencias(filtered);
        setShowSugerencias(filtered.length > 0);
        return;
      }
    }
    setShowSugerencias(false);
  };

  const selectSugerencia = (residente) => {
    const val = texto;
    const textarea = textoRef.current;
    if (!textarea) return;
    
    const pos = textarea.selectionStart;
    const beforeCursor = val.substring(0, pos);
    const hashIndex = beforeCursor.lastIndexOf('#');
    const before = val.substring(0, hashIndex);
    const after = val.substring(pos);
    const tag = residente.apartamento?.replace(/\s+/g, '') || 'apto';
    const newVal = `${before}#${tag} ${after}`;
    
    setTexto(newVal);
    setShowSugerencias(false);
    
    // Actualizar hashtags
    const tags = [];
    const regex = /#(\w+)/g;
    let match;
    while ((match = regex.exec(newVal)) !== null) {
      tags.push(match[1]);
    }
    setHashtags([...new Set(tags)]);
    
    // Re-enfocar el textarea al final del tag
    setTimeout(() => {
      textarea.focus();
      const newPos = before.length + tag.length + 2; // +1 por #, +1 por espacio
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  // Cerrar sugerencias al hacer click fuera
  useEffect(() => {
    const handleClick = (e) => {
      if (textoRef.current && !textoRef.current.contains(e.target)) {
        setShowSugerencias(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!categoria) { setError('Selecciona una categoría'); return; }
    if (!texto.trim() && fotos.length === 0) { setError('Escribe una descripción o adjunta fotos'); return; }

    setLoading(true); setError('');
    try {
      const fotoUrls = [];
      for (const foto of fotos) {
        const ts = Date.now();
        const r = ref(storage, `reportes/general_${ts}.jpg`);
        const blob = await fetch(foto).then(r => r.blob());
        const snap = await uploadBytes(r, blob);
        fotoUrls.push(await getDownloadURL(snap.ref));
      }
      await createReport({
        texto: texto,
        fotos: fotoUrls,
        categoria,
        tipo: 'general',
        hashtags,
        residenteId: user.uid,
        autor_nombre: user.nombre_completo,
      });
      setSuccess('Reporte enviado exitosamente');
      setTimeout(() => onSuccess?.(), 1500);
    } catch (err) { setError(translateError(err)); }
    setLoading(false);
  };

  return (
    <div className="main-content">
      <div className="page-header flex-between">
        <div>
          <h1>📝 Nuevo Reporte</h1>
          <p>Reporta averías, ruidos, daños u otras incidencias</p>
        </div>
        <button className="btn btn-outline" onClick={onBack}>Volver</button>
      </div>
      <div className="card" style={{ maxWidth: 620 }}>
        <div className="card-header"><h2>📋 Datos del reporte</h2></div>
        {error && <div className="alert alert-error">⚠️ {error}</div>}
        {success && <div className="alert alert-success">✅ {success}</div>}
        {!success && (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Categoría *</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                {CATEGORIAS.map((cat) => (
                  <button key={cat.value} type="button"
                    onClick={() => setCategoria(cat.value)}
                    style={{
                      padding: '10px 12px', borderRadius: 12, border: categoria === cat.value
                        ? `2px solid ${cat.color}` : '1px solid var(--border)',
                      background: categoria === cat.value
                        ? `${cat.color}15` : 'rgba(255,255,255,0.5)',
                      cursor: 'pointer', fontSize: '0.82rem', fontWeight: categoria === cat.value ? 700 : 500,
                      color: categoria === cat.value ? cat.color : 'var(--text-secondary)',
                      transition: 'all 0.2s', textAlign: 'left'
                    }}>
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Descripción *</label>
              <div style={{ position: 'relative' }}>
                <textarea
                  ref={textoRef}
                  value={texto}
                  onChange={handleTextChange}
                  placeholder="Describe el problema. Usa # para etiquetar un apartamento, ej: #B14"
                  rows={5}
                  style={{
                    width: '100%', borderRadius: 'var(--radius-sm)', padding: 12,
                    border: '1px solid var(--border)', fontFamily: 'inherit',
                    fontSize: '0.9rem', resize: 'vertical', lineHeight: 1.5
                  }}
                />
                {showSugerencias && sugerencias.length > 0 && (
                  <div style={{
                    position: 'absolute', bottom: '100%', left: 0, right: 0,
                    background: '#fff', borderRadius: 12, boxShadow: '0 10px 40px rgba(0,0,0,0.18)',
                    border: '1px solid var(--border)', zIndex: 50, maxHeight: 200,
                    overflow: 'auto', marginBottom: 4
                  }}>
                    <div style={{
                      padding: '6px 12px', fontSize: '0.65rem', color: 'var(--text-light)',
                      textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700,
                      borderBottom: '1px solid #f1f5f9'
                    }}>
                      � Apartamentos
                    </div>
                    {sugerencias.map((r) => (
                      <div key={r.id}
                        onMouseDown={(e) => { e.preventDefault(); selectSugerencia(r); }}
                        style={{
                          padding: '12px 14px', cursor: 'pointer', display: 'flex',
                          alignItems: 'center', gap: 10, borderBottom: '1px solid #f8fafc',
                          transition: 'background 0.1s',
                          background: 'transparent'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(99,102,241,0.06)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{
                          width: 34, height: 34, borderRadius: 10,
                          background: 'linear-gradient(135deg, var(--primary), var(--primary-2))',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.8rem', fontWeight: 700, color: 'var(--primary)',
                          border: '2px solid rgba(99,102,241,0.2)'
                        }}>
                          #{r.apartamento?.substring(0, 2) || 'AP'}
                        </div>
                        <div>
                          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)' }}>
                            <span style={{ color: 'var(--primary)', fontWeight: 700 }}>#{r.apartamento}</span>
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            P{r.piso} · {r.nombre_completo?.split(' ')[0] || 'Residente'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                  }}
                />
                <p style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                  💡 Escribe <strong>#</strong> seguido del número de apartamento para etiquetarlo
                </p>
              </div>
            </div>
            {hashtags.length > 0 && (
              <div className="mb-16">
                <label style={{ fontWeight: 700, fontSize: '0.78rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 8, display: 'block' }}>
                  🏠 Apartamentos etiquetados
                </label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {hashtags.map((tag) => (
                    <span key={tag} style={{
                      background: 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(139,92,246,0.08))',
                      color: 'var(--primary)', padding: '4px 14px', borderRadius: 20,
                      fontSize: '0.8rem', fontWeight: 600, border: '1px solid rgba(99,102,241,0.2)'
                    }}>
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="mb-16">
              <label style={{ fontWeight: 700, fontSize: '0.78rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 8, display: 'block' }}>
                📸 Fotos (opcional)
              </label>
              {fotos.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                  {fotos.map((f, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      <img src={f} alt={`Foto ${i + 1}`} style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover', border: '2px solid var(--border)' }} />
                      <button type="button" className="btn btn-ghost btn-sm"
                        onClick={() => setFotos(fotos.filter((_, j) => j !== i))}
                        style={{ position: 'absolute', top: -6, right: -6, background: 'var(--danger)', color: '#fff', borderRadius: '50%', width: 22, height: 22, fontSize: '0.6rem', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <input ref={reportFileRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleFoto} />
              <button type="button" className="btn btn-outline btn-full" onClick={() => reportFileRef.current?.click()}>
                📎 Adjuntar foto
              </button>
            </div>
            <button type="submit" className="btn btn-danger btn-full" disabled={loading}>
              {loading ? 'Enviando...' : '🚩 Enviar Reporte'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}